"use client";

import { ChevronDown, Hammer, X } from "lucide-react";
import { Icon } from "@/components/ui/Icon";
import { SIGNUP_TRADE_GROUPS } from "@/lib/signup/trade-gate";
import { tradeLabel } from "@/lib/trades/account-trades";

/**
 * The signup trade picker — a MULTI-select since 11 September 2026.
 *
 * ## Why more than one
 *
 * A trades business is rarely one thing. Signup only admits heating, plumbing,
 * HVAC and renewables, and inside that funnel a gas engineer who also does
 * plumbing and bathrooms is the ordinary case rather than an edge of it. The
 * workspace has held a LIST since the settings picker landed
 * (`accounts.trades`, main trade first) — signup was the one place still
 * insisting on a single answer, so every multi-trade owner started with the
 * wrong half of their job types and had to go and find Settings to fix it.
 *
 * ## Why it is still a native <select>
 *
 * Same reasoning as the single picker it replaces: ~60 options over ten groups
 * on a phone is exactly the job the platform picker is good at, and the only
 * one that gets the iOS wheel and the Android list for free. So the control
 * stays a `<select>` and the MULTIPLE part is a list of what has been picked
 * above it — the same shape Settings → Business → Details already uses, which
 * is worth copying over inventing a second idiom for one question.
 *
 * 🚨 The select resets to its placeholder after every pick, so it reads as "add
 * another" rather than as the current value. It is not showing a choice, it is
 * making one; a select that keeps the last pick selected invites somebody to
 * change it and quietly lose the trade they had.
 *
 * The FIRST trade picked is the main one, and it is labelled as such on screen.
 * `accounts.trade` is load-bearing in a dozen readers that have nothing to do
 * with this list (admin filters, suggested qualifications, onboarding card
 * targeting), so which one it is has to be visible rather than incidental.
 */
export function TradeSelect({
  value,
  onChange,
  error,
  id,
}: {
  /** Every trade picked, main first. */
  value: string[];
  onChange: (value: string[]) => void;
  error?: string | null;
  id?: string;
}) {
  const picked = value;
  const add = (trade: string) => {
    if (!trade || picked.includes(trade)) return;
    onChange([...picked, trade]);
  };
  const remove = (trade: string) => onChange(picked.filter((t) => t !== trade));
  const makeMain = (trade: string) => onChange([trade, ...picked.filter((t) => t !== trade)]);

  return (
    <div className="mucka-auth-field">
      {picked.length > 0 && (
        <ul className="mucka-auth-trades">
          {picked.map((trade, i) => (
            <li
              key={trade}
              className={`mucka-auth-trade${i === 0 ? " mucka-auth-trade--main" : ""}`}
            >
              <span className="mucka-auth-trade-label">{tradeLabel(trade)}</span>
              {i === 0 ? (
                <span className="mucka-auth-trade-tag">Main</span>
              ) : (
                <button
                  type="button"
                  className="mucka-auth-trade-btn"
                  onClick={() => makeMain(trade)}
                >
                  Make main
                </button>
              )}
              <button
                type="button"
                className="mucka-auth-trade-x"
                aria-label={`Remove ${tradeLabel(trade)}`}
                onClick={() => remove(trade)}
              >
                <Icon icon={X} size={14} strokeWidth={2.5} />
              </button>
            </li>
          ))}
        </ul>
      )}

      <div
        className={[
          "mucka-auth-input-wrap",
          "mucka-auth-input-wrap--has-trail",
          error && "mucka-auth-input-wrap--error",
        ]
          .filter(Boolean)
          .join(" ")}
      >
        <span className="mucka-auth-input-icon">
          <Icon icon={Hammer} size={18} strokeWidth={2} />
        </span>
        <select
          id={id}
          className="mucka-auth-select is-placeholder"
          value=""
          onChange={(e) => add(e.target.value)}
        >
          <option value="" disabled>
            {picked.length === 0 ? "Choose your trade" : "Add another trade"}
          </option>
          {SIGNUP_TRADE_GROUPS.map((group) => {
            const options = group.trades.filter((t) => !picked.includes(t.value));
            if (options.length === 0) return null;
            return (
              <optgroup key={group.category} label={group.category}>
                {options.map((trade) => (
                  <option key={trade.value} value={trade.value}>
                    {trade.label}
                  </option>
                ))}
              </optgroup>
            );
          })}
        </select>
        <span className="mucka-auth-select-chev" aria-hidden>
          <Icon icon={ChevronDown} size={18} />
        </span>
      </div>
      {error && <div className="mucka-auth-field-error">{error}</div>}
    </div>
  );
}
